import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, createSite, fetchSites } from "../lib/api-client";
import { ErrorAlert } from "../components/Alert";
import type { Site } from "../lib/types";

export function SitesPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    setIsLoading(true);
    fetchSites(tenantId)
      .then(setSites)
      .catch((err) => setError(err instanceof ApiError ? err.message : "サイト一覧の取得に失敗しました"))
      .finally(() => setIsLoading(false));
  }, [tenantId]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) return;
    setCreateError(null);
    setIsCreating(true);
    try {
      const site = await createSite(tenantId, { name, domain });
      setSites((prev) => [...prev, site]);
      setName("");
      setDomain("");
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "サイトの作成に失敗しました");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold mb-4">サイト一覧</h1>
        <ErrorAlert message={error} />
        {isLoading ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : sites.length === 0 ? (
          <p className="text-sm text-gray-500">サイトがまだ登録されていません</p>
        ) : (
          <ul className="divide-y divide-gray-200 bg-white border border-gray-200 rounded-lg">
            {sites.map((site) => (
              <li key={site.id} className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
                <div>
                  <Link to={`/sites/${site.id}`} className="text-blue-600 hover:underline font-medium">
                    {site.name}
                  </Link>
                  <p className="text-xs text-gray-500">{site.domain}</p>
                </div>
                <code className="text-xs text-gray-400">{site.siteKey}</code>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-md">
        <h2 className="text-base font-semibold mb-3">新規サイト登録</h2>
        <ErrorAlert message={createError} />
        <form onSubmit={handleCreate} className="space-y-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="site-name">
              サイト名
            </label>
            <input
              id="site-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 鈴木接骨院 LP"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="site-domain">
              ドメイン
            </label>
            <input
              id="site-domain"
              required
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="例: ss-suzuki.com"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <button
            type="submit"
            disabled={isCreating}
            className="rounded bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isCreating ? "作成中..." : "サイトを作成"}
          </button>
        </form>
      </div>
    </div>
  );
}
